import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import type { EpiChatSendPayload, EpiCheckpoint } from '../types/epi-chat-types';

/**
 * Structured consent signal sent on the ``choice`` wire field when the
 * researcher clicks Process, followed by the checkpoint id — the same shape as
 * ``checkpoint_approve:<checkpointId>``. The backend does not act on it: the
 * model starts the run from the click's message text.
 */
export const CHECKPOINT_PROCESS_CHOICE_PREFIX = 'checkpoint_process:';

const PROCESS_ANALYSIS_ID_PATTERN = /analysis_profile_id='([^']+)'/;

/**
 * The analysis a ``process_analysis`` checkpoint is about, read from its
 * ``proposedNext`` (``process_analysis(analysis_profile_id='<id>')``), which the
 * backend sets when it surfaces the prompt. The envelope has no separate id field,
 * and the prompt's message does not always carry an analysis resource card.
 * Null when ``proposedNext`` has no id.
 */
export const getProcessAnalysisProfileId = (checkpoint: EpiCheckpoint): string | null =>
  PROCESS_ANALYSIS_ID_PATTERN.exec(checkpoint.proposedNext)?.[1] ?? null;

interface ProcessAnalysisPromptProps {
  checkpoint: EpiCheckpoint;
  onAnswer: (answer: EpiChatSendPayload) => void;
  /** Analysis name from the sibling analysis resource card, when one rendered. */
  assetName: string | null;
  /** Render the Process button. True only while this checkpoint is the latest,
   *  unanswered message — a newer message supersedes the pending gate. */
  showProcess: boolean;
  disabled?: boolean;
  /** The analysis has unsaved edits on its page; processing runs the saved version. */
  hasStagedChanges?: boolean;
}

/**
 * Prompt for the ``process_analysis`` checkpoint: a review line plus a single
 * Process button. Processing is started by a chat message carrying
 * ``{ text, checkpointId, choice }``; there is no Edit affordance — to change
 * the analysis the researcher types into chat.
 */
export const ProcessAnalysisPrompt = ({
  checkpoint,
  onAnswer,
  assetName,
  showProcess,
  disabled,
  hasStagedChanges = false,
}: ProcessAnalysisPromptProps) => {
  const intl = useIntl();
  return (
    <Box data-testid="process-analysis-prompt">
      <Typography
        variant="body2"
        data-testid="process-analysis-review-prompt-copy"
        sx={{ marginBottom: showProcess ? 1.5 : 0 }}
      >
        {assetName
          ? intl.formatMessage(
              {
                id: 'epiChat.processAnalysis.reviewPrompt',
                defaultMessage: 'The analysis {name} is ready. Would you like to process it?',
              },
              { name: assetName }
            )
          : intl.formatMessage({
              id: 'epiChat.processAnalysis.reviewPromptGeneric',
              defaultMessage: 'The analysis is ready. Would you like to process it?',
            })}
      </Typography>
      {showProcess && (
        <Tooltip
          title={
            hasStagedChanges
              ? intl.formatMessage({
                  id: 'epiChat.processAnalysis.stagedChangesTooltip',
                  defaultMessage:
                    'Save or revert your changes before processing — only the saved version is processed.',
                })
              : ''
          }
        >
          {/* A disabled button emits no hover events, so the tooltip hangs off a wrapper. */}
          <Box component="span" sx={{ display: 'inline-block' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              data-testid="process-analysis-button"
              disabled={disabled || hasStagedChanges}
              onClick={() =>
                onAnswer({
                  text: intl.formatMessage({
                    id: 'epiChat.processAnalysis.processedMessage',
                    defaultMessage: 'Process this analysis.',
                  }),
                  checkpointId: checkpoint.checkpointId,
                  choice: `${CHECKPOINT_PROCESS_CHOICE_PREFIX}${checkpoint.checkpointId}`,
                })
              }
            >
              {intl.formatMessage({
                id: 'epiChat.processAnalysis.processButton',
                defaultMessage: 'Process',
              })}
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
